import {
  classifyToddlerSpeech,
  type Decision,
  type NumberAnswer,
} from "./toddlerPhonetics";

export type InputMode = "IDLE" | "PARTICIPATION" | "CLAP" | "SEMANTIC" | "TOUCH_ONLY";

export interface LatencyReport {
  tA: number | null; // Acoustic detection -> ACKNOWLEDGE (target ~300ms, null if unmeasured)
  tB: number | null; // Speech end -> Recognition complete (null if unmeasured)
  tC: number | null; // Judgment -> Semantic reaction (null if unmeasured)
  total: number | null;
}

export interface ChildInputObserver {
  readonly onVoiceActivity?: (rms: number) => void;
  readonly onAcknowledge?: (latencyMs: number | null) => void;
  readonly onClapDetected?: (latencyMs: number | null) => void;
  readonly onParticipationDetected?: (latencyMs: number | null) => void;
  readonly onSemanticResult?: (decision: Decision, transcript: string, latencies: LatencyReport) => void;
  readonly onSilenceTimeout?: () => void;
  readonly onMicPermissionChanged?: (granted: boolean, error?: string) => void;
}

export class UnifiedChildInput {
  #audioContext: AudioContext | null = null;
  #stream: MediaStream | null = null;
  #analyser: AnalyserNode | null = null;
  #animFrame: number | null = null;
  #activeMode: InputMode = "IDLE";
  #expectedNumber: NumberAnswer = 1;
  #isTeacherSpeaking = false;
  #micGranted = false;
  #speechRecognition: any = null;
  #silenceTimer: number | null = null;
  #lastClapTime = 0;
  #speechFrames = 0;

  // Two-Phase Communication Timestamps
  #acousticTriggerTime = 0;
  #speechEndTime = 0;
  #listeningStartTime = 0;
  #acknowledged = false;

  constructor(readonly observer: ChildInputObserver = {}) {}

  get isMicGranted(): boolean {
    return this.#micGranted;
  }

  get activeMode(): InputMode {
    return this.#activeMode;
  }

  async initMicrophone(): Promise<boolean> {
    if (this.#stream !== null) return true;
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      this.observer.onMicPermissionChanged?.(false, "UNSUPPORTED");
      return false;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          autoGainControl: true,
          echoCancellation: true,
          noiseSuppression: true,
          channelCount: 1,
        },
        video: false,
      });

      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      const ctx = new AudioCtx();
      if (ctx.state === "suspended") {
        await ctx.resume();
      }

      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      analyser.smoothingTimeConstant = 0.6;
      source.connect(analyser);

      this.#stream = stream;
      this.#audioContext = ctx;
      this.#analyser = analyser;
      this.#micGranted = true;
      this.observer.onMicPermissionChanged?.(true);

      this.#startAcousticLoop();
      this.#setupWebSpeech();
      return true;
    } catch (err: any) {
      this.#micGranted = false;
      const reason = err?.name || "PERMISSION_DENIED";
      this.observer.onMicPermissionChanged?.(false, reason);
      return false;
    }
  }

  #setupWebSpeech(): void {
    // Q08: Under Cloud STT HOLD, unverified remote webkitSpeechRecognition is disabled.
    // Audio processing is 100% local Web Audio API AnalyserNode (local VAD & claps).
    this.#speechRecognition = null;
  }

  setTeacherSpeaking(speaking: boolean): void {
    this.#isTeacherSpeaking = speaking;
    if (speaking) {
      this.#clearSilenceTimer();
    } else {
      this.#listeningStartTime = performance.now();
    }
  }

  setMode(mode: InputMode, expectedNumber: NumberAnswer = 1, silenceTimeoutMs = 7000): void {
    this.#activeMode = mode;
    this.#expectedNumber = expectedNumber;
    this.#speechFrames = 0;
    this.#acknowledged = false;
    this.#acousticTriggerTime = 0;
    this.#speechEndTime = 0;
    this.#listeningStartTime = performance.now();
    this.#clearSilenceTimer();

    if (mode === "IDLE" || mode === "TOUCH_ONLY") {
      return;
    }

    // Set silence safety timer (LT-G)
    if (silenceTimeoutMs > 0) {
      this.#silenceTimer = window.setTimeout(() => {
        if (!this.#isTeacherSpeaking && this.#activeMode !== "IDLE") {
          this.observer.onSilenceTimeout?.();
        }
      }, silenceTimeoutMs);
    }
  }

  #clearSilenceTimer(): void {
    if (this.#silenceTimer !== null) {
      window.clearTimeout(this.#silenceTimer);
      this.#silenceTimer = null;
    }
  }

  #startAcousticLoop(): void {
    if (this.#animFrame !== null) return;

    const sample = () => {
      this.#animFrame = requestAnimationFrame(sample);
      if (!this.#analyser || this.#isTeacherSpeaking) {
        this.observer.onVoiceActivity?.(0);
        return;
      }

      const buffer = new Float32Array(this.#analyser.fftSize);
      this.#analyser.getFloatTimeDomainData(buffer);

      let sum = 0;
      let peak = 0;
      for (let i = 0; i < buffer.length; i++) {
        const val = buffer[i] ?? 0;
        sum += val * val;
        const abs = Math.abs(val);
        if (abs > peak) peak = abs;
      }
      const rms = Math.sqrt(sum / buffer.length);
      this.observer.onVoiceActivity?.(rms);

      const now = performance.now();

      // 1. Two-Phase Phase 1: Immediate Acknowledge on ANY Acoustic Activity (Q04: Unmeasured T-A = null)
      if (rms >= 0.035 && !this.#acknowledged && this.#activeMode !== "IDLE") {
        this.#acknowledged = true;
        this.#acousticTriggerTime = now;
        this.observer.onAcknowledge?.(null);
      }

      // 2. Clap / Transient Detection (Q04: Unmeasured T-A = null)
      if (this.#activeMode === "CLAP") {
        if (peak >= 0.12 && rms >= 0.04 && now - this.#lastClapTime > 350) {
          this.#lastClapTime = now;
          this.#clearSilenceTimer();
          this.observer.onClapDetected?.(null);
        }
      }

      // 3. Participation / Imitation Detection (Q04: Unmeasured T-A = null)
      if (this.#activeMode === "PARTICIPATION") {
        if (rms >= 0.035) {
          this.#speechFrames += 1;
          if (this.#speechFrames >= 5) {
            this.#speechFrames = 0;
            this.#clearSilenceTimer();
            this.observer.onParticipationDetected?.(null);
          }
        } else {
          this.#speechFrames = Math.max(0, this.#speechFrames - 1);
        }
      }
    };

    this.#animFrame = requestAnimationFrame(sample);
  }

  dispose(): void {
    this.#clearSilenceTimer();
    if (this.#animFrame !== null) {
      cancelAnimationFrame(this.#animFrame);
      this.#animFrame = null;
    }
    try {
      this.#speechRecognition?.abort();
    } catch {}
    this.#speechRecognition = null;

    if (this.#stream) {
      for (const track of this.#stream.getTracks()) {
        track.stop();
      }
      this.#stream = null;
    }

    if (this.#audioContext && this.#audioContext.state !== "closed") {
      void this.#audioContext.close();
      this.#audioContext = null;
    }
    this.#analyser = null;
    this.#micGranted = false;
  }
}
